import { useState, useEffect } from "react";
import { FaChevronLeft, FaCheckCircle, FaKey } from "react-icons/fa";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  FormItem,
  PasswordInput,
  useForm,
} from "@/frontend/components/common";
import { authService } from "@/frontend/services";
import { buildPasswordResetAppUrl } from "@/frontend/utils/appDeepLink";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);
  const [form] = useForm();

  const appResetUrl = token ? buildPasswordResetAppUrl(token) : "";

  useEffect(() => {
    if (!token) {
      navigate("/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile) return;

    const timer = window.setTimeout(() => {
      window.location.href = appResetUrl;
    }, 350);

    return () => window.clearTimeout(timer);
  }, [token, appResetUrl]);

  const handleSubmit = async (values: any) => {
    if (values.password !== values.confirmPassword) {
      form.setFields([
        {
          name: "confirmPassword",
          errors: ["No coinciden"],
        },
      ]);
      return;
    }

    try {
      await authService.resetPassword({
        token: token!,
        newPassword: values.password,
      });
      setIsSuccess(true);
    } catch (error: any) {
      form.setFields([
        {
          name: "password",
          errors: [error.response?.data?.error || "Error de seguridad"],
        },
      ]);
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-slate-50 p-6 font-sans relative overflow-hidden">
      {/* Visual Background */}
      <div className="absolute inset-0 z-0 opacity-10">
        <img 
          src="/stadium-bg.png" 
          alt="Stadium" 
          className="absolute inset-0 w-full h-full object-cover grayscale"
        />
      </div>

      <div className="relative z-10 w-full max-w-lg flex flex-col items-center">
        {!isSuccess ? (
          <div className="w-full bg-white rounded-3xl p-10 md:p-14 border border-slate-100 shadow-2xl shadow-slate-200">
             <div className="mb-10 text-center">
                <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FaKey className="text-emerald-600 text-2xl" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">
                  Nueva contraseña
                </h1>
                <p className="text-slate-500 font-medium text-lg">
                  Establece una nueva clave de acceso segura.
                </p>
             </div>

            <Form form={form} onFinish={handleSubmit} layout="vertical" className="space-y-6">
              <FormItem
                name="password"
                label={<span className="text-slate-700 text-sm font-semibold ml-1">Contraseña Nueva</span>}
                rules={[
                  { required: true, message: "Campo requerido" },
                  { min: 8, message: "Mínimo 8 caracteres" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const hasNumber = /[0-9]/.test(value);
                      const hasLetter = /[a-zA-Z]/.test(value);
                      const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(value);
                      if (!hasNumber || !hasLetter || !hasSpecial) return Promise.reject("Combina letras, números y símbolos");
                      return Promise.resolve();
                    },
                  }
                ]}
              >
                <PasswordInput
                  placeholder="••••••••"
                  size="large"
                  className="bg-slate-50 border-slate-200 text-slate-900 h-14 rounded-xl focus:border-emerald-500 focus:bg-white transition-all"
                />
              </FormItem>

              <FormItem
                name="confirmPassword"
                label={<span className="text-slate-700 text-sm font-semibold ml-1">Confirmar Contraseña</span>}
                rules={[{ required: true, message: "Repite la clave" }]}
              >
                <PasswordInput
                  placeholder="••••••••"
                  size="large"
                  className="bg-slate-50 border-slate-200 text-slate-900 h-14 rounded-xl focus:border-emerald-500 focus:bg-white transition-all"
                />
              </FormItem>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                className="h-14 bg-emerald-600 hover:bg-emerald-500 border-none rounded-xl text-lg font-bold shadow-lg shadow-emerald-100 text-white transition-all transform active:scale-[0.98]"
              >
                Actualizar contraseña
              </Button>
            </Form>

            <Link to="/login" className="flex items-center justify-center gap-2 text-slate-400 hover:text-emerald-600 transition-colors mt-8 font-bold text-sm">
              <FaChevronLeft className="text-[10px]" /> 
              <span>Cancelar</span>
            </Link>
          </div>
        ) : (
          <div className="w-full bg-white rounded-3xl p-10 md:p-14 border border-slate-100 shadow-2xl shadow-slate-200 text-center">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-emerald-100">
              <FaCheckCircle className="text-emerald-500 w-10 h-10" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight">¡Éxito!</h2>
            <p className="text-slate-500 font-medium mb-10 text-lg">Tu contraseña ha sido actualizada con éxito.</p>
            
            <Link to="/login">
              <Button type="primary" size="large" block className="h-14 bg-slate-900 hover:bg-slate-800 text-white border-none rounded-xl font-bold transition-all transform active:scale-[0.95]">
                Ir al inicio de sesión
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
